<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4">
          <v-card-title class="text-indigo text-button">Importar certificado</v-card-title>

          <v-form ref="formRef" @submit.prevent="save">
            <v-container fluid>
              <!-- ── mecanismo ─────────────────────────────────────────── -->
              <v-row>
                <v-col cols="12">
                  <h4 class="text-button text-primary">Mecanismo</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-card
                          :color="mech === 'pem' ? 'blue-lighten-5' : undefined"
                          :variant="mech === 'pem' ? 'flat' : 'outlined'"
                          @click="mech = 'pem'"
                        >
                          <v-card-title class="text-center">Importar con PEM</v-card-title>
                          <v-card-text>
                            El certificado ya fue emitido a partir de un CSR generado por
                            el banco. La llave privada ya existe acá.
                          </v-card-text>
                        </v-card>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-card
                          :color="mech === 'p12' ? 'blue-lighten-5' : undefined"
                          :variant="mech === 'p12' ? 'flat' : 'outlined'"
                          @click="mech = 'p12'"
                        >
                          <v-card-title class="text-center">Importar con p12</v-card-title>
                          <v-card-text>
                            Importa el certificado y su llave privada desde un archivo p12.
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>

              <!-- ── PEM ───────────────────────────────────────────────── -->
              <v-row v-if="mech === 'pem'">
                <v-col cols="12">
                  <h4 class="text-button text-primary">Llave y CSR de origen</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-autocomplete
                          v-model="selectedKey"
                          v-model:search="keySearch"
                          :items="keyItems"
                          :loading="loadingKeys"
                          label="Llave"
                          prepend-inner-icon="mdi-key"
                          :disabled="saving"
                          no-data-text="Escribe para buscar una llave por alias"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-select
                          v-model="csrId"
                          :items="csrItems"
                          label="CSR"
                          prepend-inner-icon="mdi-file-certificate"
                          :disabled="!selectedKey || saving"
                          :loading="loadingCsr"
                          :error-messages="errors.for('csr_id')"
                          hint="El certificado se asocia a este CSR."
                          persistent-hint
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>

                <v-col cols="12">
                  <h4 class="text-button text-primary">Certificado</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" lg="6">
                        <v-file-input
                          v-model="certFile"
                          accept=".pem,.crt,.cer,.der,.p7b,.p7s,.ca-bundle"
                          label="Archivo del certificado"
                          prepend-icon=""
                          prepend-inner-icon="mdi-file-upload"
                          show-size
                          :disabled="saving"
                          hint="También puedes pegar el PEM abajo."
                          persistent-hint
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="pem"
                          label="PEM"
                          rows="8"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('pem')"
                          placeholder="-----BEGIN CERTIFICATE-----"
                          @update:model-value="errors.clear('pem')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>

              <!-- ── p12 ───────────────────────────────────────────────── -->
              <v-row v-else>
                <v-col cols="12">
                  <h4 class="text-button text-primary">Archivo p12</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-file-input
                          v-model="p12File"
                          accept=".p12,.pfx"
                          label="Archivo p12"
                          prepend-icon=""
                          prepend-inner-icon="mdi-file-lock"
                          show-size
                          :disabled="saving"
                          :rules="[rules.required]"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="p12.password"
                          label="Contraseña del p12"
                          type="password"
                          prepend-inner-icon="mdi-form-textbox-password"
                          :disabled="!p12File || saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('password')"
                          @update:model-value="errors.clear('password')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-select
                          v-model="p12.certificate_mapper_id"
                          :items="mapperItems"
                          :loading="loadingMappers"
                          label="Mapeador de certificado"
                          prepend-inner-icon="mdi-swap-horizontal"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('certificate_mapper_id')"
                          hint="Define cómo se traducen los datos del certificado a campos de usuario."
                          persistent-hint
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="p12.alias_certificate"
                          label="Alias del certificado (opcional)"
                          prepend-inner-icon="mdi-tag"
                          :disabled="!p12File || saving"
                          hint="Si se omite, se importa el primer certificado del archivo."
                          persistent-hint
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="p12.alias_key"
                          label="Alias de la llave (opcional)"
                          prepend-inner-icon="mdi-key"
                          :disabled="!p12File || saving"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>
            </v-container>

            <v-card-actions>
              <v-spacer />
              <v-btn
                type="submit"
                variant="text"
                color="success"
                class="mx-1"
                :loading="saving"
              >
                <v-icon class="mr-1">mdi-content-save</v-icon>Importar
              </v-btn>
              <v-btn
                variant="text"
                color="error"
                class="mx-1"
                :disabled="saving"
                @click="router.push('/admin/certificates')"
              >
                Cancelar
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
// CertificateCreatePayload es ahora una unión discriminada que cubre las dos
// ramas. En bancojs 1.x la variante p12 no se exportaba desde el índice, lo que
// obligaba a `import ... from "bancojs/dist/resource/Certificate"` — un import
// profundo a un dist versionado que además estaba desactualizado.
import type { CertificateCreatePayload } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useFormErrors } from "@/composables/useFormErrors";
import { useNotificationsStore } from "@/stores/notifications";
import { debounce } from "@/utils/functions";
import { csrStateLabel } from "@/utils/labels";

const route = useRoute();
const router = useRouter();
const { report } = useApiErrors();
const notifications = useNotificationsStore();
const errors = useFormErrors();

const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

const mech = ref<"pem" | "p12">("pem");
const saving = ref(false);

// ---- rama PEM -------------------------------------------------------------
const selectedKey = ref<number | undefined>();
const keySearch = ref("");
const keyItems = ref<Array<{ title: string; value: number }>>([]);
const loadingKeys = ref(false);

const csrId = ref<number | undefined>();
const csrItems = ref<Array<{ title: string; value: number; props?: { disabled: boolean } }>>([]);
const loadingCsr = ref(false);

const certFile = ref<File | File[] | null>(null);
const pem = ref("");

// ---- rama p12 -------------------------------------------------------------
const p12File = ref<File | File[] | null>(null);
const p12 = ref({
  password: "",
  certificate_mapper_id: undefined as number | undefined,
  alias_certificate: "",
  alias_key: "",
});
const mapperItems = ref<Array<{ title: string; value: number }>>([]);
const loadingMappers = ref(false);

const rules = { required: (val: unknown) => !!val || "Campo requerido." };

async function searchKeys(term: string) {
  loadingKeys.value = true;
  try {
    const page = await banco.admin.keys.find({ ...(term ? { alias: term } : {}), size: 20 });
    keyItems.value = page.content.map((k) => ({
      title: `${k.alias ?? "(sin alias)"} — id ${k.id}`,
      value: k.id,
    }));
  } catch (err) {
    report(err, { title: "Llaves", fallback: "No fue posible buscar llaves." });
  } finally {
    loadingKeys.value = false;
  }
}
watch(keySearch, debounce((term: string) => void searchKeys(term), 300));

watch(selectedKey, async (id) => {
  csrId.value = undefined;
  csrItems.value = [];
  if (!id) return;
  loadingCsr.value = true;
  try {
    const list = await banco.admin.keys.listCsr(id);
    csrItems.value = list.map((c) => ({
      title: `CSR ${c.id} — ${csrStateLabel(c.state)}`,
      value: c.id,
      // Un CSR ya completado tiene certificado: no admite otro.
      props: { disabled: c.state === "COMPLETED" },
    }));
  } catch (err) {
    report(err, { title: "CSR", fallback: "No fue posible recuperar los CSR de la llave." });
  } finally {
    loadingCsr.value = false;
  }
});

/** Al elegir un archivo se vuelca su contenido en el textarea del PEM. */
watch(certFile, async (file) => {
  const picked = Array.isArray(file) ? file[0] : file;
  if (picked) pem.value = await picked.text();
});

async function loadMappers() {
  loadingMappers.value = true;
  try {
    const page = await banco.admin.mappers.find({ size: 100 });
    mapperItems.value = page.content.map((m) => ({ title: m.name, value: m.id }));
  } catch (err) {
    report(err, { title: "Mapeadores", fallback: "No fue posible recuperar los mapeadores." });
  } finally {
    loadingMappers.value = false;
  }
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // readAsDataURL entrega "data:...;base64,XXXX"; la API espera sólo XXXX.
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function save() {
  const validation = await formRef.value?.validate();
  if (validation && !validation.valid) return;

  errors.reset();
  saving.value = true;
  try {
    let payload: CertificateCreatePayload;

    if (mech.value === "pem") {
      payload = {
        pem: pem.value,
        state: "ACTIVE",
        ...(csrId.value ? { csr_id: csrId.value } : {}),
      };
    } else {
      const picked = Array.isArray(p12File.value) ? p12File.value[0] : p12File.value;
      if (!picked) return;
      payload = {
        state: "ACTIVE",
        p12: {
          base64: await fileToBase64(picked),
          password: p12.value.password,
          certificate_mapper_id: p12.value.certificate_mapper_id as number,
          alias_certificate: p12.value.alias_certificate || undefined,
          alias_key: p12.value.alias_key || undefined,
        },
      };
    }

    const cert = await banco.admin.certificates.create(payload);
    notifications.success(`Se importó el certificado ${cert.fingerprint}.`, "Creado");
    router.push("/admin/certificates");
  } catch (err) {
    report(err, {
      title: "Certificado",
      fallback: "Ocurrió un error al importar el certificado.",
      form: errors,
    });
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  await Promise.all([searchKeys(""), loadMappers()]);
  // Precarga desde el enlace "Importar Certificado" de la vista de CSR.
  const keyParam = Number(route.query.key_id);
  const csrParam = Number(route.query.csr_id);
  if (Number.isFinite(keyParam) && keyParam > 0) {
    selectedKey.value = keyParam;
    if (Number.isFinite(csrParam) && csrParam > 0) {
      // Esperar a que el watcher cargue los CSR antes de fijar el valor.
      await new Promise((r) => setTimeout(r, 0));
      csrId.value = csrParam;
    }
  }
});
</script>
